/* eslint-disable no-restricted-properties */
import { createFeatureSelector, createSelector } from '@ngrx/store';

import { PurchaseState, ValidationChecks } from './purchase.state';

import { CartConstants, CartItem, CartProductTypes } from '@api/cart/cart.models';
import { CartService } from '@api/cart/cart.service';
import { Order } from '@api/order/order.models';
import { CurrencyInfo } from '@core/domain/models/store.models';
import { getCurrencyInfo, getCurrencyInfoAlternates, getStoreDetails } from '@core/infrastructure/state/core.selectors';
import { formatPriceWithCurrencySymbol, getPrimaryPriceWithAlternateFormatted } from '@frontend/common/currency';

const selectPurchaseState = createFeatureSelector<PurchaseState>('purchase');

export const getPurchaseOrderPreviewItems = createSelector(
    selectPurchaseState,
    (purchaseAttemptState: PurchaseState) => purchaseAttemptState?.orderPreview.values?.[0]?.items,
);

export const getPurchaseAttempt = createSelector(
    selectPurchaseState,
    (purchaseAttemptState: PurchaseState) => purchaseAttemptState?.attempt.values,
);

export const getPurchaseAttemptStatus = createSelector(selectPurchaseState, (purchaseAttemptState: PurchaseState) =>
    purchaseAttemptState?.attempt.status);

export const purchaseValidationErrors = createSelector(selectPurchaseState, (purchaseAttemptState: PurchaseState) => {
    const obj = Object.entries(purchaseAttemptState?.validationChecks).reduce((acc, [key, value]) => {
        if (!value) {
            acc[key] = value;
        }
        return acc;
    }, {});
    return obj as ValidationChecks;
});

export const getPurchaseErrors = createSelector(
    selectPurchaseState,
    (purchaseAttemptState: PurchaseState) => purchaseAttemptState?.errors,
);

export const areThePurchaseValidationsOk = createSelector(selectPurchaseState, (purchaseAttemptState: PurchaseState) =>
    Object.values(purchaseAttemptState?.validationChecks).every((validationOk) => validationOk));

export const getPromotions = createSelector(
    selectPurchaseState,
    (purchaseAttemptState: PurchaseState) => purchaseAttemptState?.promotions,
);

export const getPurchaseAttemptAdjustments = createSelector(
    selectPurchaseState,
    getCurrencyInfo,
    (state: PurchaseState, currencyInfo: CurrencyInfo) => {
        return state?.attempt.values?.adjustment
            ?.filter((item) => item.type !== CartProductTypes.TYPE_GIFT)
            .map((item) => {
                return {
                    ...item,
                    priceFormatted:
                        formatPriceWithCurrencySymbol(+item.amount, currencyInfo),
                };
            });
    },
);

export const getPurchaseAttemptShippingPriceFormatted = createSelector(
    selectPurchaseState,
    getCurrencyInfo,
    (state: PurchaseState, currencyInfo: CurrencyInfo) => {
        return formatPriceWithCurrencySymbol(
            state?.attempt.values?.shippingPrice,
            currencyInfo,
        );
    },
);

// Total de items (excluyendo los regalos) del carro de compra
export const getPurchaseAttemptTotalProducts = createSelector(selectPurchaseState, (state: PurchaseState) => {
    return CartService.getItemsQuantity(state?.attempt.values?.items);
});

// Si el carro contiene un sólo producto
export const getPurchaseAttemptIsOneProductCart = createSelector(getPurchaseAttemptTotalProducts, (total: number) => {
    return total === 1;
});

// Item regalo de un carro
export const getPurchaseAttemptCartItemGift = createSelector(selectPurchaseState, (state: PurchaseState) => {
    return state?.attempt.values?.items?.find((item) => item.name === CartConstants.XGIFT);
});

// Si el carro contiene un regalo
export const getPurchaseAttemptCartHasGift = createSelector(getPurchaseAttemptCartItemGift, (item: CartItem) => {
    return item !== undefined;
});

// si ha de mostrar la tasa del carro de compra
export const getPurchaseAttemptShowTaxes = createSelector(selectPurchaseState, (state: PurchaseState) => {
    return state?.attempt.values?.tax > 0;
});

// si el valor para tasa es nulo
export const getPurchaseAttemptTaxIsNull = createSelector(selectPurchaseState, (state: PurchaseState) => {
    return state?.attempt.values?.tax === null;
});

// Precio del regalo formateado
export const getPurchaseAttemptCartItemGiftPriceFormatted = createSelector(
    getPurchaseAttemptCartItemGift,
    getCurrencyInfo,
    (item: CartItem, currencyInfo: CurrencyInfo) => {
        if (item !== undefined) {
            return formatPriceWithCurrencySymbol(item.unitPrice, currencyInfo);
        }
        return '';
    },
);

// si ha de mostrar la lista de tasas del carro de compra
export const getPurchaseAttemptShowTaxesArray = createSelector(selectPurchaseState, (state: PurchaseState) => {
    return state?.attempt.values?.taxArray?.length > 0;
});

// valores de las tasas
export const getPurchaseAttemptTaxValueFormatted = createSelector(
    selectPurchaseState,
    getCurrencyInfo,
    getPurchaseAttemptShowTaxes,
    (state: PurchaseState, currencyInfo: CurrencyInfo, showTaxesArray: boolean) => {
        if (showTaxesArray) {
            return state?.attempt.values?.taxArray.map((item) => {
                return {
                    ...item,
                    priceFormatted:
                        formatPriceWithCurrencySymbol(item.price, currencyInfo),
                };
            });
        }
        let value: number;
        if (state?.attempt.values?.taxArray?.length > 0) {
            value = state?.attempt.values?.taxArray[0].price;
        } else {
            value = state?.attempt.values?.tax + state?.attempt.values?.shippingTax;
        }
        return formatPriceWithCurrencySymbol(value, currencyInfo);
    },
);

export const getPurchaseAttemptTotalOrder = createSelector(
    selectPurchaseState,
    (state: PurchaseState) => state?.attempt.values?.totalOrder,
);

// Total del carro formateado con la moneda local
export const getPurchaseAttemptTotalOrderFormatted = createSelector(
    selectPurchaseState,
    getCurrencyInfo,
    (state: PurchaseState, currencyInfo: CurrencyInfo) => {
        return formatPriceWithCurrencySymbol(
            state?.attempt.values?.totalOrder,
            currencyInfo,
        );
    },
);

// Total del carro formateado con la moneda principal y secundarias
export const getPurchaseAttemptTotalOrderFormattedWithAlternates = createSelector(
    selectPurchaseState,
    getCurrencyInfo,
    getCurrencyInfoAlternates,
    (state: PurchaseState, currencyInfo: CurrencyInfo, currencyInfoAlternates: CurrencyInfo[]) => {
        return getPrimaryPriceWithAlternateFormatted(
            state?.attempt.values?.totalOrder,
            currencyInfo,
            currencyInfoAlternates,
        );
    },
);

export const getMinOrderBill = createSelector(
    getStoreDetails,
    (storeDetails) => {
        if (storeDetails.minOrderBill) {
            // biome-ignore lint/style/useNumberNamespace: <explanation>
            const minOrderBill = parseInt(storeDetails.minOrderBill)
                // biome-ignore lint/style/useExponentiationOperator: <explanation>
                * Math.pow(10, Math.abs(storeDetails.currencyInfo.currencyDecimals));
            return minOrderBill;
        }
        return null;
    },
);

export const getNeedBilling = createSelector(
    selectPurchaseState,
    (purchaseAttemptState: PurchaseState) => purchaseAttemptState?.userInteraction.billing.need,
);

export const getUserInteractionBilling = createSelector(
    selectPurchaseState,
    (purchaseAttemptState: PurchaseState) => purchaseAttemptState?.userInteraction.billing,
);

export const getOrderPreviewStatus = createSelector(
    selectPurchaseState,
    (purchaseAttemptState: PurchaseState) => purchaseAttemptState?.orderPreview.status,
);

export const getSyncCheckoutLaunched = createSelector(
    selectPurchaseState,
    (purchaseAttemptState: PurchaseState) => purchaseAttemptState?.syncCheckoutLaunched,
);

export const getOrderPreviewOrder = createSelector(
    selectPurchaseState,
    (purchaseAttemptState: PurchaseState) => purchaseAttemptState?.orderPreview?.values[0],
);

export const getCardinalDDC = createSelector(
    selectPurchaseState,
    (purchaseAttemptState: PurchaseState) => purchaseAttemptState?.cardinal.ddc,
);

export const getOrderPreviewShipping = createSelector(
    getOrderPreviewOrder,
    (orderPreviewOrder: Order) => orderPreviewOrder?.shipping,
);

export const getOrderPreviewTotalOrder = createSelector(
    getOrderPreviewOrder,
    (orderPreviewOrder: Order) => orderPreviewOrder?.totalOrder,
);

export const getValidationCheckPrivacyPolicyModal = createSelector(
    selectPurchaseState,
    (purchaseAttemptState: PurchaseState) => purchaseAttemptState?.validationChecks.privacyPolicyModal,
);

export const getSelectedEgui = createSelector(
    selectPurchaseState,
    (purchaseAttemptState: PurchaseState) => purchaseAttemptState?.egui.eguiManagementType,
);

export const getEguiData = createSelector(
    selectPurchaseState,
    (purchaseAttemptState: PurchaseState) => purchaseAttemptState?.egui,
);

export const getEguiDonationCode = createSelector(
    selectPurchaseState,
    (purchaseAttemptState: PurchaseState) => purchaseAttemptState?.egui.eguiDonationCode,
);

export const getPurchaseAttemptEguiTypesAllowed = createSelector(
    selectPurchaseState,
    (purchaseAttemptState: PurchaseState) => purchaseAttemptState?.attempt.values.eguiData.managementTypesAllowed,
);
